/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/require-default-props */
import { ActionIcon, Badge, Divider, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconScale } from '@tabler/icons-react';
import { EntityTypeType, UnitOfMeasureType } from '../../../../models/core/cache.type';
import { Action } from '../../../../models/core/core.type';
import {
  ProposalResiduePlanDestinationType,
  ProposalResiduePlanEquipmentType,
  ProposalResiduePlanPackagingType,
  ProposalResiduePlanQuotationDestinationType,
  ProposalResiduePlanQuotationEquipmentType,
  ProposalResiduePlanQuotationPackagingType,
  ProposalResiduePlanQuotationTreatmentType,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanQuotationVehicleType,
  ProposalResiduePlanTreatmentType,
  ProposalResiduePlanType,
  ProposalResiduePlanVehicleType,
  ProposalServiceType,
} from '../../../../models/core/proposals.type';
import DeleteConfirmPopover from '../../../../components/DeleteConfirmPopover';
import ResiduePlanQuotationSummary from './ResiduePlanQuotationSummary';

type ResidueQuotationSummaryProps = {
  data: ProposalResiduePlanType;
  compra?: boolean | null;
  isEdit?: boolean;
  requireSupplier?: boolean;
  hidePackagingSection?: boolean;
  hideEquipmentSection?: boolean;
  hideVehicleSection?: boolean;
  hideTreatmentSection?: boolean;
  hideDestinationSection?: boolean;
  referenceData?: {
    entityTypeData?: EntityTypeType[];
    unitOfMeasures: UnitOfMeasureType[];
  };
  services?: ProposalServiceType[];
  zIndex?: number;
  onAdd?: () => void;
  onSetPrimary?: (quotation: ProposalResiduePlanQuotationType) => void;
  onQuotationAction: (quotation: ProposalResiduePlanQuotationType) => void;
  onToleranceAction: (quotation: ProposalResiduePlanQuotationType) => void;
  onDelete?: (quotation: ProposalResiduePlanQuotationType) => void;
  onItemQuotationAction?: (
    quotation: ProposalResiduePlanQuotationType,
    quotationTab: string,
    itemId: string | number
  ) => void;
  onItemDeleteAction?: (
    quotation: ProposalResiduePlanQuotationType,
    quotationTab: string,
    itemId: string | number
  ) => void;
  onItemToleranceAction?: (
    quotation: ProposalResiduePlanQuotationType,
    quotationTab: string,
    itemId: string | number
  ) => void;
  onAddItemQuotationAction?: (quotation: ProposalResiduePlanQuotationType, quotationTab: string) => void;
  onEquipmentQuotationSubmit?: (
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanEquipmentType,
    item: ProposalResiduePlanQuotationEquipmentType | null,
    action: string
  ) => void;
  onPackagingQuotationSubmit?: (
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanPackagingType,
    item: ProposalResiduePlanQuotationPackagingType | null,
    action: string
  ) => void;
  onVehicleQuotationSubmit?: (
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanVehicleType,
    item: ProposalResiduePlanQuotationVehicleType | null,
    action: string
  ) => void;
  onTreatmentQuotationSubmit?: (
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanTreatmentType,
    item: ProposalResiduePlanQuotationTreatmentType | null,
    action: string
  ) => void;
  onDestinationQuotationSubmit?: (
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanDestinationType,
    item: ProposalResiduePlanQuotationDestinationType | null,
    action: string
  ) => void;
};

export default function ResidueQuotationSummary(props: ResidueQuotationSummaryProps) {
  const visibleQuotations = [...props.data.cotacoes].filter(
    (x) => (x.action || Action.Nothing) !== Action.Delete
  );

  // const actionLabel = props.isEdit ? 'Editar cotação' : 'Ver cotação';
  // const ActionIconComponent = props.isEdit ? IconEdit : IconEye;

  const countTolerances = (quotation: ProposalResiduePlanQuotationType) =>
    quotation.acondicionamentos.reduce((acc, item) => acc + (item.tolerancias?.length || 0), 0) +
    quotation.equipamentos.reduce((acc, item) => acc + (item.tolerancias?.length || 0), 0) +
    (quotation.veiculo?.tolerancias?.length || 0) +
    (quotation.tratamento?.tolerancias?.length || 0) +
    (quotation.destinoFinal?.tolerancias?.length || 0);

  const matchesByKey = (
    left: string | number | null | undefined,
    right: string | number | null | undefined
  ) => {
    if (
      left === null ||
      left === undefined ||
      right === null ||
      right === undefined ||
      left === '' ||
      right === ''
    ) {
      return false;
    }

    return `${left}` === `${right}`;
  };

  const matchesPlanItem = (
    planItem: {
      id?: string | number | null;
      idTemporaria?: string | number | null;
      relatedId?: string | number | null;
      residueId?: string | number | null;
    },
    quotationItem: {
      id?: string | number | null;
      idTemporaria?: string | number | null;
      relatedId?: string | number | null;
      residueId?: string | number | null;
    }
  ) =>
    matchesByKey(planItem.relatedId, quotationItem.relatedId) ||
    matchesByKey(planItem.id, quotationItem.relatedId) ||
    matchesByKey(planItem.idTemporaria, quotationItem.relatedId) ||
    matchesByKey(planItem.id, quotationItem.id) ||
    matchesByKey(planItem.id, quotationItem.idTemporaria) ||
    matchesByKey(planItem.idTemporaria, quotationItem.id) ||
    matchesByKey(planItem.idTemporaria, quotationItem.idTemporaria) ||
    matchesByKey(planItem.residueId, quotationItem.residueId);

  const isQuotationEffectivelyComplete = (quotation: ProposalResiduePlanQuotationType) => {
    const activePlanPackagings = props.hidePackagingSection
      ? []
      : props.data.acondicionamentos.filter((x) => (x.action || Action.Nothing) !== Action.Delete);
    const activePlanEquipments = props.hideEquipmentSection
      ? []
      : props.data.equipamentos.filter((x) => (x.action || Action.Nothing) !== Action.Delete);
    const activeQuotationPackagings = quotation.acondicionamentos.filter(
      (x) => (x.action || Action.Nothing) !== Action.Delete
    );
    const activeQuotationEquipments = quotation.equipamentos.filter(
      (x) => (x.action || Action.Nothing) !== Action.Delete
    );

    const hasPendingPackaging = activePlanPackagings.some(
      (planItem) =>
        !activeQuotationPackagings.some((quotationItem) =>
          matchesPlanItem(
            {
              id: planItem.id,
              idTemporaria: planItem.idTemporaria,
              relatedId: planItem.idPropostaResiduoPlanoAcondicionamento,
              residueId: planItem.idResiduoAcondicionamento,
            },
            {
              id: quotationItem.id,
              idTemporaria: quotationItem.idTemporaria,
              relatedId: quotationItem.idPropostaResiduoPlanoAcondicionamento,
              residueId: quotationItem.idResiduoAcondicionamento,
            }
          )
        )
    );

    const hasPendingEquipment = activePlanEquipments.some(
      (planItem) =>
        !activeQuotationEquipments.some((quotationItem) =>
          matchesPlanItem(
            {
              id: planItem.id,
              idTemporaria: planItem.idTemporaria,
              relatedId: planItem.idPropostaResiduoPlanoEquipamento,
              residueId: planItem.idResiduoEquipamento,
            },
            {
              id: quotationItem.id,
              idTemporaria: quotationItem.idTemporaria,
              relatedId: quotationItem.idPropostaResiduoPlanoEquipamento,
              residueId: quotationItem.idResiduoEquipamento,
            }
          )
        )
    );

    const hasPendingVehicle =
      !props.hideVehicleSection &&
      !!props.data.veiculo &&
      (props.data.veiculo.action || Action.Nothing) !== Action.Delete &&
      (!quotation.veiculo ||
        (quotation.veiculo.action || Action.Nothing) === Action.Delete ||
        !matchesPlanItem(
          {
            id: props.data.veiculo.id,
            idTemporaria: props.data.veiculo.idTemporaria,
            relatedId: props.data.veiculo.idPropostaResiduoPlanoVeiculo,
            residueId: props.data.veiculo.idResiduoVeiculo,
          },
          {
            id: quotation.veiculo.id,
            idTemporaria: quotation.veiculo.idTemporaria,
            relatedId: quotation.veiculo.idPropostaResiduoPlanoVeiculo,
            residueId: quotation.veiculo.idResiduoVeiculo,
          }
        ));

    const hasPendingTreatment =
      !props.hideTreatmentSection &&
      !!props.data.tratamento &&
      (props.data.tratamento.action || Action.Nothing) !== Action.Delete &&
      (!quotation.tratamento ||
        (quotation.tratamento.action || Action.Nothing) === Action.Delete ||
        !matchesPlanItem(
          {
            id: props.data.tratamento.id,
            idTemporaria: props.data.tratamento.idTemporaria,
            relatedId: props.data.tratamento.idPropostaResiduoPlanoTratamento,
            residueId: props.data.tratamento.idResiduoTratamento,
          },
          {
            id: quotation.tratamento.id,
            idTemporaria: quotation.tratamento.idTemporaria,
            relatedId: quotation.tratamento.idPropostaResiduoPlanoTratamento,
            residueId: quotation.tratamento.idResiduoTratamento,
          }
        ));

    const hasPendingDestination =
      !props.hideDestinationSection &&
      !!props.data.destinoFinal &&
      (props.data.destinoFinal.action || Action.Nothing) !== Action.Delete &&
      (!quotation.destinoFinal ||
        (quotation.destinoFinal.action || Action.Nothing) === Action.Delete ||
        !matchesPlanItem(
          {
            id: props.data.destinoFinal.id,
            idTemporaria: props.data.destinoFinal.idTemporaria,
            relatedId: props.data.destinoFinal.idPropostaResiduoPlanoDestinoFinal,
            residueId: props.data.destinoFinal.idResiduoDestinoFinal,
          },
          {
            id: quotation.destinoFinal.id,
            idTemporaria: quotation.destinoFinal.idTemporaria,
            relatedId: quotation.destinoFinal.idPropostaResiduoPlanoDestinoFinal,
            residueId: quotation.destinoFinal.idResiduoDestinoFinal,
          }
        ));

    return !(
      hasPendingPackaging ||
      hasPendingEquipment ||
      hasPendingVehicle ||
      hasPendingTreatment ||
      hasPendingDestination
    );
  };

  const hasAnyQuotationItem = (quotation: ProposalResiduePlanQuotationType) =>
    (!props.hidePackagingSection &&
      quotation.acondicionamentos.some((x) => (x.action || Action.Nothing) !== Action.Delete)) ||
    (!props.hideEquipmentSection &&
      quotation.equipamentos.some((x) => (x.action || Action.Nothing) !== Action.Delete)) ||
    (!props.hideVehicleSection &&
      !!quotation.veiculo &&
      (quotation.veiculo.action || Action.Nothing) !== Action.Delete) ||
    (!props.hideTreatmentSection &&
      !!quotation.tratamento &&
      (quotation.tratamento.action || Action.Nothing) !== Action.Delete) ||
    (!props.hideDestinationSection &&
      !!quotation.destinoFinal &&
      (quotation.destinoFinal.action || Action.Nothing) !== Action.Delete);

  return (
    <Stack gap="sm">
      {!visibleQuotations.length && (
        <Text size="sm" c="dimmed">
          Nenhuma cotação cadastrada.
        </Text>
      )}

      {visibleQuotations.map((quotation) => {
        const isComplete = isQuotationEffectivelyComplete(quotation);

        return (
          <>
            <Divider variant="dashed" />

            <Stack
              key={quotation.idPropostaResiduoPlanoCotacao || quotation.idTemporaria || quotation.id}
              gap="sm"
            >
              <Group justify="space-between">
                <Group gap="xs" className="custom-mantine-badge-group-down">
                  <Badge variant="outline" size="sm">
                    Cotação #{quotation.idPropostaResiduoPlanoCotacao || '-'}
                  </Badge>
                  {quotation.primario && (
                    <Badge color="green" variant="light" size="sm">
                      Primária
                    </Badge>
                  )}
                  {!isComplete && (
                    <Badge color="orange" variant="light" size="sm">
                      Incompleta
                    </Badge>
                  )}
                  <Group gap={3}>
                    <IconScale size={14} />
                    <Badge variant="outline" size="sm">
                      {countTolerances(quotation)}
                    </Badge>
                  </Group>
                </Group>

                <Group gap={4}>
                  {props.isEdit && !quotation.primario && props.onSetPrimary && (
                    <Tooltip label="Definir como primário" withArrow>
                      <ActionIcon
                        size="sm"
                        color="green"
                        variant="light"
                        disabled={!isComplete || !hasAnyQuotationItem(quotation)}
                        onClick={() => props.onSetPrimary!(quotation)}
                      >
                        <IconCheck size={15} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {/* <Tooltip label={actionLabel} withArrow>
                    <ActionIcon size="sm" variant="light" onClick={() => props.onQuotationAction(quotation)}>
                      <ActionIconComponent size={15} />
                    </ActionIcon>
                  </Tooltip> */}
                  {props.isEdit && props.onDelete && (
                    <DeleteConfirmPopover
                      label="Excluir cotação"
                      message="Esta ação removerá a cotação do plano."
                      onConfirm={() => props.onDelete!(quotation)}
                    />
                  )}
                </Group>
              </Group>

              <ResiduePlanQuotationSummary
                data={props.data}
                compra={props.compra}
                quotation={quotation}
                isEdit={props.isEdit}
                hidePackagingSection={props.hidePackagingSection}
                hideEquipmentSection={props.hideEquipmentSection}
                hideVehicleSection={props.hideVehicleSection}
                hideTreatmentSection={props.hideTreatmentSection}
                hideDestinationSection={props.hideDestinationSection}
                referenceData={props.referenceData}
                services={props.services}
                zIndex={props.zIndex}
                requireSupplier={props.requireSupplier}
                onItemQuotationAction={props.onItemQuotationAction}
                onItemDeleteAction={props.onItemDeleteAction}
                onItemToleranceAction={props.onItemToleranceAction}
                onAddItemQuotationAction={props.onAddItemQuotationAction}
                onEquipmentQuotationSubmit={
                  props.onEquipmentQuotationSubmit
                    ? (refItem, item, action) =>
                        props.onEquipmentQuotationSubmit!(quotation, refItem, item, action)
                    : undefined
                }
                onPackagingQuotationSubmit={
                  props.onPackagingQuotationSubmit
                    ? (refItem, item, action) =>
                        props.onPackagingQuotationSubmit!(quotation, refItem, item, action)
                    : undefined
                }
                onVehicleQuotationSubmit={
                  props.onVehicleQuotationSubmit
                    ? (refItem, item, action) =>
                        props.onVehicleQuotationSubmit!(quotation, refItem, item, action)
                    : undefined
                }
                onTreatmentQuotationSubmit={
                  props.onTreatmentQuotationSubmit
                    ? (refItem, item, action) =>
                        props.onTreatmentQuotationSubmit!(quotation, refItem, item, action)
                    : undefined
                }
                onDestinationQuotationSubmit={
                  props.onDestinationQuotationSubmit
                    ? (refItem, item, action) =>
                        props.onDestinationQuotationSubmit!(quotation, refItem, item, action)
                    : undefined
                }
              />
            </Stack>
          </>
        );
      })}
    </Stack>
  );
}
